import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FiX, FiRotateCw, FiCheck, FiCamera } from 'react-icons/fi';

function PhotoSignatureCapture({ value, onChange, onClose }) {
  const [capturedImage, setCapturedImage] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Iniciar la cámara inmediatamente
  const startCamera = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Detener stream anterior si existe
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      // Verificar soporte de cámara
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Cámara no soportada en este dispositivo');
      }

      // Solicitar acceso a la cámara con configuración específica para móviles
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      };

      console.log('Solicitando acceso a cámara con constraints:', constraints);
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Esperar a que los metadatos estén cargados
        videoRef.current.onloadedmetadata = () => {
          console.log('Metadatos de video cargados');
          videoRef.current.play()
            .then(() => {
              console.log('Video iniciado correctamente');
              setIsLoading(false);
            })
            .catch(err => {
              console.error('Error al reproducir video:', err);
              setError('No se pudo iniciar la cámara');
              setIsLoading(false);
            });
        };
        
        // Manejar errores de video
        videoRef.current.onerror = (e) => {
          console.error('Error en video:', e);
          setError('Error al cargar el video de la cámara');
          setIsLoading(false);
        };
      }
    } catch (err) {
      console.error('Error al acceder a la cámara:', err);
      setError(`Error: ${err.message || 'No se pudo acceder a la cámara'}`);
      setIsLoading(false);
    }
  }, [facingMode]);

  // Capturar foto
  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    const context = canvas.getContext('2d');
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(imageData);
    
    // Detener la cámara
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  }, []);

  // Cambiar cámara
  const switchCamera = useCallback(() => {
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
  }, [facingMode]);

  // Confirmar foto
  const confirmPhoto = useCallback(() => {
    if (capturedImage) {
      onChange(capturedImage);
      onClose();
    }
  }, [capturedImage, onChange, onClose]);

  // Retomar foto
  const retakePhoto = useCallback(() => {
    setCapturedImage(null);
    setError(null);
    startCamera();
  }, [startCamera]);

  // Iniciar cámara al montar el componente
  useEffect(() => {
    startCamera();
    
    return () => {
      // Limpiar al desmontar
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [startCamera]);

  // Reiniciar cámara cuando cambia el modo
  useEffect(() => {
    if (!capturedImage) {
      startCamera();
    }
  }, [facingMode, capturedImage, startCamera]);

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="bg-black bg-opacity-90 text-white p-4 flex justify-between items-center">
        <h3 className="text-lg font-semibold">Capturar Firma</h3>
        <button 
          onClick={onClose} 
          className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
        >
          <FiX className="w-6 h-6" />
        </button>
      </div>

      {/* Camera View / Image Preview */}
      <div className="flex-1 relative overflow-hidden">
        <canvas ref={canvasRef} className="hidden" />
        
        {capturedImage ? (
          /* Imagen capturada */
          <div className="w-full h-full flex items-center justify-center bg-black">
            <img 
              src={capturedImage} 
              alt="Firma capturada" 
              className="max-w-full max-h-full object-contain" 
            />
          </div>
        ) : (
          /* Video de la cámara */
          <div className="relative w-full h-full bg-black">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
                  <p className="text-lg">Accediendo a la cámara...</p>
                </div>
              </div>
            )}
            
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted 
              className="w-full h-full object-cover"
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
            />
            
            {/* Overlay guía */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute inset-4 border-2 border-white border-opacity-50 rounded-lg"></div>
            </div>
            
            {/* Instrucciones */}
            <div className="absolute bottom-20 left-0 right-0 text-center">
              <div className="bg-black bg-opacity-70 text-white px-4 py-2 rounded-full mx-4">
                <p className="text-sm">Coloque la firma dentro del marco</p>
              </div>
            </div>
            
            {/* Error message */}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80 z-20">
                <div className="text-white text-center p-6 max-w-md">
                  <div className="bg-red-500 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <FiCamera className="w-8 h-8" />
                  </div>
                  <p className="text-sm mb-6 text-gray-300">{error}</p>
                  <button 
                    onClick={startCamera}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Reintentar
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="bg-black bg-opacity-90 p-6">
        <div className="flex justify-center items-center gap-6">
          {capturedImage ? (
            /* Controles para imagen capturada */
            <>
              <button 
                onClick={retakePhoto}
                className="px-6 py-3 bg-white bg-opacity-20 text-white rounded-full hover:bg-opacity-30 transition-colors"
              >
                Tomar otra
              </button>
              <button 
                onClick={confirmPhoto}
                className="px-8 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors flex items-center gap-2"
              >
                <FiCheck className="w-5 h-5" />
                Usar esta foto
              </button>
            </>
          ) : !error ? (
            /* Controles para captura */
            <>
              <button 
                onClick={switchCamera}
                className="p-3 bg-white bg-opacity-20 text-white rounded-full hover:bg-opacity-30 transition-colors"
                title="Cambiar cámara"
              >
                <FiRotateCw className="w-6 h-6" />
              </button>
              <button 
                onClick={capturePhoto}
                className="p-4 bg-white rounded-full hover:bg-gray-200 transition-colors shadow-lg"
                title="Capturar foto"
                disabled={isLoading}
              >
                <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                  <div className="w-12 h-12 bg-white rounded-full"></div>
                </div>
              </button>
              <div className="w-12"></div> {/* Spacer */}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default PhotoSignatureCapture;